import { Flex, Text } from '@backstage/ui';
import type { RemixiconComponentType } from '@remixicon/react';
import { IconLabel } from '../ui';
import { displayedPercent, usageLevel } from './platform';

export interface MeterProps {
  label: string;
  /**
   * The meter's accessible name when `label` alone would be ambiguous —
   * « CPU de 8xjnh » on a node row that shows « CPU ». `label` otherwise.
   */
  accessibleLabel?: string;
  /** Before the visible label only; never part of the accessible name. */
  icon?: RemixiconComponentType;
  valueText: string;
  ratio: number;
  /** The ratio as a percentage, formatted in the page's locale. */
  percentText: string;
  compact?: boolean;
  /**
   * `stacked` (the default): the label and the figures on a line, the bar
   * under them. `row`: one line of three columns — the label, a compact bar,
   * the figures then the percentage in bold, coloured from the warning line
   * on — the node rows of the « Nœuds » drawer (PlatformNodes.tsx), whose
   * CPU and memory lines align column by column.
   */
  layout?: 'stacked' | 'row';
  /**
   * Forces the tone where the warning is not a share of the ceiling — a
   * node pool's bar is in warning only once it has reached its maximum
   * (« Pools de nœuds »). Computed from `ratio` otherwise (`usageLevel`:
   * amber from `WARNING_PERCENT`, red from `CRITICAL_PERCENT`). `brand`: a
   * share that is neither good nor bad — a cost item's part of the total
   * (« Coûts », components/projectCosts) —, in the accent colour, never in
   * warning.
   */
  tone?: 'normal' | 'warning' | 'critical' | 'brand';
  /**
   * A second position on the same scale, drawn as a thin line across the bar
   * — the request, on the resources cards. Decorative for assistive
   * technology: `label` is its tooltip, and the cards say the same figure in
   * words next to the bar.
   */
  marker?: { ratio: number; label: string };
}

const LEVEL_TONES = {
  ok: 'normal',
  warning: 'warning',
  critical: 'critical',
} as const;

/** The BUI text colour of the figures in each tone. */
const TEXT_COLORS = {
  normal: 'secondary',
  brand: 'secondary',
  warning: 'warning',
  critical: 'danger',
} as const;

/**
 * One bar against a ceiling. A `meter` for assistive technology, with the
 * figures as its text; the warning colour from `WARNING_PERCENT`, the danger
 * colour from `CRITICAL_PERCENT` (or as `tone` says) is carried by the value
 * text too, not by the bar alone.
 *
 * Shared by the node rows and node pools of the home page's platform strip
 * (requested versus allocatable, nodes versus maximum) and the resources cards
 * of the Odoo pages (used versus limit). Laid out by `.waadoo-home-meter*` in
 * styles.css.
 */
export function Meter(props: MeterProps) {
  const tone = props.tone ?? LEVEL_TONES[usageLevel(props.ratio)];
  const percent = displayedPercent(props.ratio);
  const label = props.icon ? (
    <IconLabel icon={props.icon}>{props.label}</IconLabel>
  ) : (
    props.label
  );
  const bar = (
    <div
      role="meter"
      aria-label={props.accessibleLabel ?? props.label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(percent, 100)}
      aria-valuetext={`${props.valueText} (${props.percentText})`}
      className="waadoo-home-meter"
      data-tone={tone}
      data-compact={
        props.compact || props.layout === 'row' ? 'true' : undefined
      }
    >
      <div
        className="waadoo-home-meter__fill"
        style={{ width: `${Math.min(props.ratio, 1) * 100}%` }}
      />
      {props.marker && (
        <div
          aria-hidden="true"
          title={props.marker.label}
          className="waadoo-home-meter__marker"
          style={{ left: `${Math.min(props.marker.ratio, 1) * 100}%` }}
        />
      )}
    </div>
  );
  if (props.layout === 'row') {
    return (
      <div className="waadoo-home-meter-row">
        <Text variant="body-small" color="secondary" truncate>
          {label}
        </Text>
        {bar}
        <Text variant="body-small" className="waadoo-home-meter-row__value">
          <span className="waadoo-home-meter-row__figures">
            {props.valueText}
          </span>{' '}
          <span
            className="waadoo-home-meter-row__percent"
            data-tone={tone === 'brand' ? undefined : tone}
          >
            {props.percentText}
          </span>
        </Text>
      </div>
    );
  }
  return (
    <div className="waadoo-home-meter-field">
      <Flex justify="between" gap="2">
        <Text variant="body-small" truncate>
          {label}
        </Text>
        <Text variant="body-small" color={TEXT_COLORS[tone]}>
          {props.valueText} · {props.percentText}
        </Text>
      </Flex>
      {bar}
    </div>
  );
}
